import { useState, useEffect, useCallback } from 'react';
/**
 * Hook for managing user favorites with localStorage persistence
 * @param moduleKey - Unique key for the module (e.g., 'user-management', 'identity')
 * @param userId - Optional user ID for user-specific favorites
 * @returns Object with favorites state and management functions
 */
export function useFavorites(moduleKey, userId) {
    const [favorites, setFavorites] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    // Generate storage key based on module and user
    const storageKey = userId
        ? `textile-erp-favorites-${moduleKey}-${userId}`
        : `textile-erp-favorites-${moduleKey}`;
    // Load favorites from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const parsedFavorites = JSON.parse(stored);
                if (Array.isArray(parsedFavorites)) {
                    setFavorites(parsedFavorites);
                }
            }
        }
        catch (error) {
            console.error('Failed to load favorites from localStorage:', error);
        }
        finally {
            setIsLoading(false);
        }
    }, [storageKey]);
    // Save favorites to localStorage whenever favorites change
    const saveFavorites = useCallback((newFavorites) => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(newFavorites));
        }
        catch (error) {
            console.error('Failed to save favorites to localStorage:', error);
        }
    }, [storageKey]);
    // Add a favorite
    const addFavorite = useCallback((path) => {
        setFavorites(prev => {
            if (prev.includes(path)) {
                return prev; // Already exists
            }
            const newFavorites = [...prev, path];
            saveFavorites(newFavorites);
            return newFavorites;
        });
    }, [saveFavorites]);
    // Remove a favorite
    const removeFavorite = useCallback((path) => {
        setFavorites(prev => {
            const newFavorites = prev.filter(fav => fav !== path);
            saveFavorites(newFavorites);
            return newFavorites;
        });
    }, [saveFavorites]);
    // Toggle favorite status
    const toggleFavorite = useCallback((path, isFavorite) => {
        const shouldAdd = isFavorite ?? !favorites.includes(path);
        if (shouldAdd) {
            addFavorite(path);
        }
        else {
            removeFavorite(path);
        }
    }, [favorites, addFavorite, removeFavorite]);
    // Check if a path is favorited
    const isFavorite = useCallback((path) => {
        return favorites.includes(path);
    }, [favorites]);
    // Clear all favorites
    const clearFavorites = useCallback(() => {
        setFavorites([]);
        saveFavorites([]);
    }, [saveFavorites]);
    return {
        favorites,
        isLoading,
        addFavorite,
        removeFavorite,
        toggleFavorite,
        isFavorite,
        clearFavorites
    };
}
